import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { Navigation } from "@/layout";
import Content from "../../base/layout/content/Content";
import useMeta from "../../base/hooks/useMeta";
import useForm from "../../base/hooks/form/useForm";
import useValidator from "../../base/hooks/form/useValidator";
import useFormActions from "../../base/hooks/form/useFormActions";
import {
    MUTATION_CREATE_ACCOUNT,
    MUTATION_UPDATE_ACCOUNT,
    QUERY_GET_ACCOUNT,
    QUERY_ACCOUNT_LIST,
} from "../../apollo/module-operations/business_account";
import routes from "../../config/routes/routes";
import StoreFormModal from "./StoreFormModal";
import AccountStores from "./AccountStores";
import AccountUsers from "../user/AccountUsers";
import { PlusIcon } from "../store/shared";
import styles from "../store/GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const BusinessAccountForm = () => {
    const { resourceId, isCreate } = useMeta();

    const [loading, setLoading] = useState(!isCreate);
    const [showStoreForm, setShowStoreForm] = useState(false);

    const { inputs, setInputs, inputChange } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "first_name",
                method: "isEmpty",
                validWhen: false,
                message: "This first name is required.",
            },
            {
                field: "last_name",
                method: "isEmpty",
                validWhen: false,
                message: "This last name is required.",
            },
            {
                field: "company",
                method: "isEmpty",
                validWhen: false,
                message: "This company is required.",
            },
            {
                field: "phone",
                method: "isEmpty",
                validWhen: false,
                message: "This phone is required.",
            },
            {
                field: "email",
                method: "isEmpty",
                validWhen: false,
                message: "This email is required.",
            },
            {
                field: "address1",
                method: "isEmpty",
                validWhen: false,
                message: "This address1 is required.",
            },
            {
                field: "city",
                method: "isEmpty",
                validWhen: false,
                message: "This city is required.",
            },
            {
                field: "province_code",
                method: "isEmpty",
                validWhen: false,
                message: "This state code is required.",
            },
            {
                field: "country_code",
                method: "isEmpty",
                validWhen: false,
                message: "This country code is required.",
            },
            {
                field: "zip",
                method: "isEmpty",
                validWhen: false,
                message: "This zip is required.",
            },
        ],
        {
            ...inputs,
            ...inputs.address,
        },
    );

    useQuery(QUERY_GET_ACCOUNT, {
        variables: {
            id: resourceId,
        },
        skip: isCreate,
        onCompleted: resp => {
            setInputs(resp.getBusinessAccount);
            setLoading(false);
        },
    });

    const options = {
        variables: {
            input: inputs,
        },
    };

    const { createOrUpdate, loading: saving } = useFormActions(
        "id",
        "Account",
        MUTATION_CREATE_ACCOUNT,
        MUTATION_UPDATE_ACCOUNT,
        [
            {
                query: QUERY_ACCOUNT_LIST,
            },
        ],
        options,
        routes.ACCOUNT_DETAIL.route,
        isValid,
        isSubmited,
    );

    const detailActions = useMemo(
        () => [
            {
                id: "save",
                label: isCreate ? "Create" : "Update",
                icon: <SaveIcon />,
                loading: saving,
                onClick: () => createOrUpdate(),
            },
        ],
        [isCreate, saving, createOrUpdate],
    );

    const storesActions = useMemo(
        () => [
            {
                id: "create-store",
                label: "Create Store",
                icon: <PlusIcon />,
                onClick: () => setShowStoreForm(true),
            },
        ],
        [],
    );

    const detailToolbar = (
        <HeaderActions
            actions={detailActions}
            aria-label="Account form actions"
        />
    );

    const storesToolbar = (
        <HeaderActions actions={storesActions} aria-label="Stores actions" />
    );

    const detailInvalid =
        validator.company.isInvalid ||
        validator.first_name.isInvalid ||
        validator.last_name.isInvalid ||
        validator.phone.isInvalid ||
        validator.email.isInvalid ||
        validator.address1.isInvalid ||
        validator.city.isInvalid ||
        validator.province_code.isInvalid ||
        validator.zip.isInvalid ||
        validator.country_code.isInvalid;

    return (
        <>
            <Content>
                <Navigation
                    items={[
                        {
                            label: "Detail",
                            icon: "fab fa-wpforms",
                            invalid: detailInvalid,
                            render: (
                                <Panel
                                    className={styles.root}
                                    header={
                                        <div className={styles.headerBar}>
                                            <div className={styles.headerText}>
                                                <Heading level={4}>
                                                    Form
                                                </Heading>
                                                <Text
                                                    tone="secondary"
                                                    variant="body-md"
                                                >
                                                    Provide all required
                                                    information.
                                                </Text>
                                            </div>
                                            {detailToolbar}
                                        </div>
                                    }
                                    showHeaderDivider
                                >
                                    {loading ? (
                                        <div className={styles.loading}>
                                            <LoadingProgress
                                                indeterminate
                                                indicator="circular"
                                                size="sm"
                                                title="Loading"
                                            />
                                        </div>
                                    ) : (
                                        <FormLayout>
                                            <FormLayout.Section title="Information">
                                                <Switch
                                                    checked={Boolean(
                                                        inputs.status,
                                                    )}
                                                    label="Active"
                                                    name="status"
                                                    onChange={inputChange}
                                                />
                                                <FormLayout.Grid columns={3}>
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "company",
                                                        )}
                                                        fullWidth
                                                        label="Company *"
                                                        name="company"
                                                        onChange={inputChange}
                                                        placeholder="Company"
                                                        value={
                                                            inputs.company || ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "first_name",
                                                        )}
                                                        fullWidth
                                                        label="First Name *"
                                                        name="first_name"
                                                        onChange={inputChange}
                                                        placeholder="First name"
                                                        value={
                                                            inputs.first_name ||
                                                            ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "last_name",
                                                        )}
                                                        fullWidth
                                                        label="Last Name *"
                                                        name="last_name"
                                                        onChange={inputChange}
                                                        placeholder="Last name"
                                                        value={
                                                            inputs.last_name ||
                                                            ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "phone",
                                                        )}
                                                        fullWidth
                                                        label="Phone Number *"
                                                        name="phone"
                                                        onChange={inputChange}
                                                        placeholder="Phone number"
                                                        value={
                                                            inputs.phone || ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "email",
                                                        )}
                                                        fullWidth
                                                        label="Email *"
                                                        name="email"
                                                        onChange={inputChange}
                                                        placeholder="Email"
                                                        value={
                                                            inputs.email || ""
                                                        }
                                                    />
                                                </FormLayout.Grid>
                                            </FormLayout.Section>

                                            <FormLayout.Section title="Location">
                                                <FormLayout.Grid columns={3}>
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "address1",
                                                        )}
                                                        fullWidth
                                                        label="Address 1 *"
                                                        name="address.address1"
                                                        onChange={inputChange}
                                                        placeholder="Address 1"
                                                        value={
                                                            inputs.address
                                                                ?.address1 || ""
                                                        }
                                                    />
                                                    <Input
                                                        fullWidth
                                                        label="Address 2"
                                                        name="address.address2"
                                                        onChange={inputChange}
                                                        placeholder="Address 2"
                                                        value={
                                                            inputs.address
                                                                ?.address2 || ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "city",
                                                        )}
                                                        fullWidth
                                                        label="City *"
                                                        name="address.city"
                                                        onChange={inputChange}
                                                        placeholder="City"
                                                        value={
                                                            inputs.address
                                                                ?.city || ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "province_code",
                                                        )}
                                                        fullWidth
                                                        label="State Code *"
                                                        name="address.province_code"
                                                        onChange={inputChange}
                                                        placeholder="State Code"
                                                        value={
                                                            inputs.address
                                                                ?.province_code ||
                                                            ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "zip",
                                                        )}
                                                        fullWidth
                                                        label="Zip *"
                                                        name="address.zip"
                                                        onChange={inputChange}
                                                        placeholder="Zip"
                                                        value={
                                                            inputs.address
                                                                ?.zip || ""
                                                        }
                                                    />
                                                    <Input
                                                        error={fieldError(
                                                            validator,
                                                            "country_code",
                                                        )}
                                                        fullWidth
                                                        label="Country Code *"
                                                        name="address.country_code"
                                                        onChange={inputChange}
                                                        placeholder="Country code"
                                                        value={
                                                            inputs.address
                                                                ?.country_code ||
                                                            ""
                                                        }
                                                    />
                                                </FormLayout.Grid>
                                            </FormLayout.Section>
                                        </FormLayout>
                                    )}
                                </Panel>
                            ),
                        },
                        {
                            label: "Stores",
                            icon: "fas fa-store",
                            render: <AccountStores toolbar={storesToolbar} />,
                        },
                        {
                            label: "Users",
                            icon: "fas fa-users",
                            render: <AccountUsers />,
                        },
                    ]}
                />
                <StoreFormModal
                    isOpen={showStoreForm}
                    toggle={setShowStoreForm}
                />
            </Content>
        </>
    );
};

export default BusinessAccountForm;
